import { Link, useParams } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import { useCondoContext } from '@/features/auth/use-condo-context';
import { useHistorialPaquetesUnidad } from '@/lib/hooks/use-paquetes';
import { PageContainer } from '@/components/common/page-container';
import { PageHeader } from '@/components/common/page-header';
import { EstadoPaqueteBadge } from '@/components/paquetes/estado-badge';
import { Button } from '@/components/ui/button';

function fmt(iso: string): string {
  return new Date(iso).toLocaleDateString('es-CL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Historial de paquetes por unidad.
 * Accessible to admin/conserje/comite from the unidad detail page,
 * and to residentes for their own unidades.
 * Backend enforces access — no role dispatch needed here.
 */
export function UnidadPaquetesRoute() {
  const { unidadId } = useParams<{ unidadId?: string }>();
  const { condoId } = useCondoContext();
  const { data, isLoading, isError } = useHistorialPaquetesUnidad(unidadId);

  const items = data ?? [];
  const backTo = unidadId
    ? `/app/condominios/${condoId}/unidades/${unidadId}`
    : `/app/condominios/${condoId}/unidades`;

  // Try to infer unidad number from the first item in the list
  const unidadNumero = items[0]?.unidadNumero ?? unidadId?.slice(-6) ?? '—';

  return (
    <PageContainer width="wide">
      <Button variant="ghost" asChild size="sm" className="mb-6">
        <Link to={backTo}>
          <ArrowLeft className="h-4 w-4" />
          Volver a Unidad
        </Link>
      </Button>

      <PageHeader
        eyebrow="Historial"
        title={`Paquetes — Unidad ${unidadNumero}`}
        ruleSpacing="mb-8"
      />

      {isLoading && (
        <div className="py-16 text-center text-sm text-[var(--color-ink-muted)]">
          Cargando historial…
        </div>
      )}

      {isError && (
        <div className="py-16 text-center text-sm text-[var(--color-danger)]">
          No se pudo cargar el historial de paquetes.
        </div>
      )}

      {!isLoading && !isError && items.length === 0 && (
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <span className="text-4xl">📦</span>
          <p className="text-sm text-[var(--color-ink-muted)]">
            Sin paquetes registrados para esta unidad.
          </p>
        </div>
      )}

      {!isLoading && !isError && items.length > 0 && (
        <div className="grid gap-2">
          {items.map((p) => (
            <Link
              key={p.id}
              to={`/app/condominios/${condoId}/paquetes/${p.id}`}
              className="grid grid-cols-[1fr_auto_auto] items-center gap-4 rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)] p-3 transition-shadow hover:shadow-sm"
            >
              {/* Metadata */}
              <div className="min-w-0">
                <p className="truncate font-semibold text-sm">
                  {p.destinatarioNombre}
                </p>
                <p className="truncate text-xs text-[var(--color-ink-muted)]">
                  {p.descripcion ? `${p.descripcion} · ` : ''}
                  Registrado el {fmt(p.registradoAt)}
                  {p.remitente ? ` · De: ${p.remitente}` : ''}
                </p>
                {p.trackingNumber && (
                  <p className="font-mono text-xs text-[var(--color-ink-muted)] mt-0.5 truncate">
                    {p.trackingNumber}
                  </p>
                )}
              </div>

              {/* Dates */}
              <div className="hidden sm:block text-right text-xs text-[var(--color-ink-muted)]">
                {p.retiradoAt && <p>Retirado {fmt(p.retiradoAt)}</p>}
                {p.devueltoAt && <p>Devuelto {fmt(p.devueltoAt)}</p>}
                {p.anuladoAt && <p>Anulado {fmt(p.anuladoAt)}</p>}
              </div>

              {/* Estado badge */}
              <EstadoPaqueteBadge estado={p.estado} />
            </Link>
          ))}
        </div>
      )}
    </PageContainer>
  );
}
